import { useState, useEffect, useMemo, useCallback, useRef, type RefObject } from 'react'
import {
  Box,
  Button,
  Collapse,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  Input,
  List,
  ListItem,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Radio,
  RadioGroup,
  Select,
  Spinner,
  Stack,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import { apiClient } from '@/lib/api/client'
import { mapApiRequestToRequestOption, TicketService } from '@/lib/services/ticket'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import type { RequestOption } from '@/types/ticket'
import { useTicketOptions } from '@/hooks/useTicketOptions'
import { toDatetimeLocalFromWallClock } from '@/lib/utils/dateTimeUtils'

interface ClientOption {
  id: number
  name?: string
  code?: string
}

interface ServiceTypeOption {
  id: number
  name?: string
  parent_id?: number | null | string
}

function isTopLevelServiceType(row: ServiceTypeOption): boolean {
  const parentId = row.parent_id
  return parentId == null || parentId === '' || parentId === 0 || parentId === '0'
}

function serviceTypeParentKey(row: ServiceTypeOption): string | null {
  if (isTopLevelServiceType(row)) return null
  return String(row.parent_id)
}

export interface CreateTicketRequestModalProps {
  isOpen: boolean
  onClose: () => void
  onCreated: (request: RequestOption) => void
  /** When editing an existing selected request */
  mode?: 'create' | 'edit'
  initialRequest?: RequestOption | null
  onUpdated?: (request: RequestOption) => void
}

function toRequestOption(req: Record<string, unknown>): RequestOption {
  return mapApiRequestToRequestOption(req)
}

function toDatetimeLocalValue(value?: string | null): string {
  return toDatetimeLocalFromWallClock(value)
}

function toBackendDateTime(value: string): string | null {
  if (!value || value.length < 16) return null
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return null
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const h = String(d.getHours()).padStart(2, '0')
  const min = String(d.getMinutes()).padStart(2, '0')
  const s = String(d.getSeconds()).padStart(2, '0')
  return `${y}-${m}-${day} ${h}:${min}:${s}`
}

function optionalString(value: string): string | null {
  const v = value.trim()
  return v || null
}

function isOnsiteServiceParent(parentOptions: { value: string; label: string }[], serviceParentId: string): boolean {
  if (!serviceParentId) return false
  const parent = parentOptions.find((p) => p.value === serviceParentId)
  if (!parent) return false
  return /onsite|on-site|on site|field/i.test(parent.label)
}

type CreateRequestFormValues = {
  clientId: string
  serviceParent: string
  hasChildTypes: boolean
  serviceTypeId: string
  description: string
  contactName: string
  contactNumber: string
  contactEmail: string
  requestedQuantity: number
}

function computeFormErrors(values: CreateRequestFormValues): Record<string, string> {
  const e: Record<string, string> = {}
  if (!values.clientId) e.client = 'Client is required'
  if (!values.serviceParent) e.serviceParent = 'Service Type is required'
  if (values.hasChildTypes && !values.serviceTypeId) e.serviceType = 'Subcategory is required'
  if (!values.description.trim()) e.description = 'Description is required'
  if (!values.contactName.trim()) e.contactName = 'Contact Name is required'
  const digitsOnly = values.contactNumber.replace(/\D/g, '')
  if (!values.contactNumber.trim()) e.contactNumber = 'Contact Number is required'
  else if (digitsOnly.length < 7) e.contactNumber = 'Enter a valid phone number'
  if (!values.contactEmail.trim()) e.contactEmail = 'Contact Email is required'
  else if (!/^\S+@\S+\.\S+$/.test(values.contactEmail)) e.contactEmail = 'Enter a valid email address'
  if (!values.requestedQuantity || values.requestedQuantity < 1) e.requestedQuantity = 'Must be at least 1'
  return e
}

const FORM_FIELD_FOCUS_ORDER = [
  'client',
  'serviceParent',
  'serviceType',
  'description',
  'contactName',
  'contactNumber',
  'contactEmail',
] as const

function focusFirstInvalidField(
  currentErrors: Record<string, string>,
  fieldRefMap: Record<string, RefObject<HTMLElement>>,
) {
  const firstErrorKey = FORM_FIELD_FOCUS_ORDER.find((k) => currentErrors[k])
  if (!firstErrorKey) return
  const el = fieldRefMap[firstErrorKey]?.current
  el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  el?.focus()
}

function RadioRow({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: string
  options: string[]
  onChange: (v: string) => void
}) {
  return (
    <FormControl>
      <FormLabel fontSize="sm">{label}</FormLabel>
      <RadioGroup value={value || ''} onChange={onChange}>
        <Stack direction="row" spacing={4} flexWrap="wrap">
          {options.map((opt) => (
            <Radio key={opt} value={opt} size="sm">
              {opt}
            </Radio>
          ))}
        </Stack>
      </RadioGroup>
    </FormControl>
  )
}

const emptyTriage = () => ({
  triageExpanded: false,
  storeId: '',
  storeCode: '',
  storeLabel: '',
  siteAddress: '',
  errorMessage: '',
  preferredScheduleUntil: '',
  contactAvailabilityText: '',
  workImpact: '',
  othersAffected: '',
  userScope: '',
  appScope: '',
  accessType: '',
  workPermitNeeded: '',
  permitAvailable: '',
  permitDuration: '',
  partsApproved: '',
  closeOnFix: '',
})

export function CreateTicketRequestModal({
  isOpen,
  onClose,
  onCreated,
  mode = 'create',
  initialRequest = null,
  onUpdated,
}: CreateTicketRequestModalProps) {
  const isEditMode = mode === 'edit'
  const { showSuccess, showError } = useToastNotification()
  const { options: requestSourceOptions } = useTicketOptions('request_source')
  const { codes: othersAffectedOptions } = useTicketOptions('others_affected')
  const { codes: userScopeOptions } = useTicketOptions('user_scope')
  const { codes: appScopeOptions } = useTicketOptions('app_scope')
  const { codes: accessTypeOptions } = useTicketOptions('access_type')
  const { codes: workPermitNeededOptions } = useTicketOptions('work_permit_needed')
  const { codes: permitAvailableOptions } = useTicketOptions('permit_available')
  const { codes: partsApprovedOptions } = useTicketOptions('parts_approved')
  const { codes: closeOnFixOptions } = useTicketOptions('close_on_fix')
  const [clients, setClients] = useState<ClientOption[]>([])
  const [serviceTypes, setServiceTypes] = useState<ServiceTypeOption[]>([])
  const [clientId, setClientId] = useState<string>('')
  const [serviceParent, setServiceParent] = useState<string>('')
  const [serviceTypeId, setServiceTypeId] = useState<string>('')
  const [description, setDescription] = useState('')
  const [requestedQuantity, setRequestedQuantity] = useState(1)
  const [contactName, setContactName] = useState('')
  const [contactNumber, setContactNumber] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [preferredScheduleAt, setPreferredScheduleAt] = useState('')
  const [preferredContactAt, setPreferredContactAt] = useState('')
  const [requestSource, setRequestSource] = useState('email')
  const [triage, setTriage] = useState(emptyTriage)
  const [editRequestDbId, setEditRequestDbId] = useState<number | null>(null)
  const [storeQuery, setStoreQuery] = useState('')
  const [storeResults, setStoreResults] = useState<
    Array<{ storeId?: number; storeCode: string; storeName: string; branchAddress?: string }>
  >([])
  const [storeLoading, setStoreLoading] = useState(false)
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoadingClients, setIsLoadingClients] = useState(false)
  const [isLoadingOptions, setIsLoadingOptions] = useState(false)
  const storeSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const editPrefillKeyRef = useRef<string | null>(null)
  const editEnrichKeyRef = useRef<string | null>(null)
  const clientRef = useRef<HTMLSelectElement>(null)
  const serviceParentRef = useRef<HTMLSelectElement>(null)
  const serviceTypeRef = useRef<HTMLSelectElement>(null)
  const descriptionRef = useRef<HTMLTextAreaElement>(null)
  const contactNameRef = useRef<HTMLInputElement>(null)
  const contactNumberRef = useRef<HTMLInputElement>(null)
  const contactEmailRef = useRef<HTMLInputElement>(null)

  const fieldRefMap: Record<string, RefObject<HTMLElement>> = {
    client: clientRef,
    serviceParent: serviceParentRef,
    serviceType: serviceTypeRef,
    description: descriptionRef,
    contactName: contactNameRef,
    contactNumber: contactNumberRef,
    contactEmail: contactEmailRef,
  }

  const patchTriage = (patch: Partial<ReturnType<typeof emptyTriage>>) => {
    setTriage((prev) => ({ ...prev, ...patch }))
  }

  const clientCode = useMemo(() => {
    const c = clients.find((x) => String(x.id) === clientId)
    return c?.code ?? ''
  }, [clients, clientId])

  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    setIsLoadingClients(true)
    apiClient
      .get('/options/clients')
      .then((res) => {
        if (cancelled) return
        const list = Array.isArray(res.data) ? res.data : []
        setClients(list)
      })
      .catch(() => {
        if (!cancelled) setClients([])
      })
      .finally(() => {
        if (!cancelled) setIsLoadingClients(false)
      })
    return () => {
      cancelled = true
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen || !clientCode) {
      setServiceTypes([])
      if (!isEditMode) {
        setServiceParent('')
        setServiceTypeId('')
      }
      return
    }
    setIsLoadingOptions(true)
    // Catalog endpoint (ticketing DB) — no client assignment required
    apiClient
      .get('/options/service-types')
      .then((typesRes) => {
        const types = Array.isArray(typesRes.data) ? typesRes.data : []
        setServiceTypes(types)
      })
      .catch(() => {
        setServiceTypes([])
      })
      .finally(() => setIsLoadingOptions(false))
  }, [isOpen, clientCode, isEditMode])

  const parentOptions = useMemo(() => {
    return serviceTypes
      .filter(isTopLevelServiceType)
      .map((s) => ({ value: String(s.id), label: s.name || String(s.id) }))
  }, [serviceTypes])

  const childOptions = useMemo(() => {
    if (!serviceParent) return []
    return serviceTypes
      .filter((s) => serviceTypeParentKey(s) === serviceParent)
      .map((s) => ({ value: String(s.id), label: s.name || String(s.id) }))
  }, [serviceTypes, serviceParent])

  const hasChildTypes = childOptions.length > 0
  const effectiveServiceId = hasChildTypes ? serviceTypeId : serviceParent
  const showOnsite = isOnsiteServiceParent(parentOptions, serviceParent)

  const applyServiceTypeSelection = useCallback(
    (serviceTypeIdValue?: number | null) => {
      if (!serviceTypeIdValue || !serviceTypes.length) return
      const row = serviceTypes.find((s) => Number(s.id) === Number(serviceTypeIdValue))
      if (!row) return
      if (isTopLevelServiceType(row)) {
        setServiceParent(String(row.id))
        setServiceTypeId('')
      } else {
        setServiceParent(String(row.parent_id))
        setServiceTypeId(String(row.id))
      }
    },
    [serviceTypes],
  )

  // Prefill form when opening in edit mode
  useEffect(() => {
    if (!isOpen || !isEditMode || !initialRequest) {
      if (!isOpen) {
        editPrefillKeyRef.current = null
        editEnrichKeyRef.current = null
      }
      return
    }
    const prefillKey = `${initialRequest.requestDbId ?? ''}:${initialRequest.id}`
    if (editPrefillKeyRef.current === prefillKey) return
    editPrefillKeyRef.current = prefillKey
    editEnrichKeyRef.current = null

    if (initialRequest.clientId != null) setClientId(String(initialRequest.clientId))
    setEditRequestDbId(initialRequest.requestDbId ?? null)
    setDescription(initialRequest.description || initialRequest.issueDescription || '')
    setRequestedQuantity(
      initialRequest.requestedQuantity && initialRequest.requestedQuantity > 0
        ? initialRequest.requestedQuantity
        : 1,
    )
    setContactName(initialRequest.contactName || initialRequest.clientName || '')
    setContactNumber(initialRequest.contactNumber || '')
    setContactEmail(initialRequest.contactEmail || '')
    setPreferredScheduleAt(toDatetimeLocalValue(initialRequest.preferredScheduleAt))
    setPreferredContactAt(toDatetimeLocalValue(initialRequest.preferredContactAt))
    setRequestSource(initialRequest.requestSource || 'email')
    setTriage({
      ...emptyTriage(),
      siteAddress: initialRequest.siteAddress || '',
      preferredScheduleUntil: toDatetimeLocalValue(initialRequest.preferredScheduleUntil),
      contactAvailabilityText: initialRequest.contactAvailabilityText || '',
      triageExpanded: Boolean(
        initialRequest.siteAddress ||
          initialRequest.preferredScheduleUntil ||
          initialRequest.contactAvailabilityText,
      ),
    })
  }, [isOpen, isEditMode, initialRequest])

  // Resolve service type cascade after options load (edit)
  useEffect(() => {
    if (!isOpen || !isEditMode || !initialRequest?.serviceTypeId || !serviceTypes.length) return
    applyServiceTypeSelection(initialRequest.serviceTypeId)
  }, [isOpen, isEditMode, initialRequest, serviceTypes, applyServiceTypeSelection])

  // Resolve numeric DB id + enrich from GET when client context is ready
  useEffect(() => {
    if (!isOpen || !isEditMode || !initialRequest) return
    const headerCode = initialRequest.clientCode || clientCode
    if (!headerCode) return

    const enrichKey = `${initialRequest.requestDbId ?? ''}:${initialRequest.id}:${headerCode}`
    if (editEnrichKeyRef.current === enrichKey) return
    editEnrichKeyRef.current = enrichKey

    let cancelled = false

    const resolveAndEnrich = async () => {
      let dbId = initialRequest.requestDbId ?? null

      if (!dbId && initialRequest.id) {
        try {
          const searchRes = await TicketService.searchRequestOptions(String(initialRequest.id))
          const match = searchRes.data?.find((r) => String(r.id) === String(initialRequest.id))
          if (match?.requestDbId) {
            dbId = match.requestDbId
            if (!cancelled) {
              setEditRequestDbId(dbId)
              if (match.serviceTypeId) applyServiceTypeSelection(match.serviceTypeId)
            }
          }
        } catch {
          // ignore — submit will surface missing id
        }
      }

      if (!dbId) return
      if (!cancelled) setEditRequestDbId(dbId)

      try {
        const res = await apiClient.get(`/support/my-request/${dbId}`, {
          headers: { 'X-Client-Code': headerCode },
        })
        if (cancelled) return
        const raw = (res.data?.data ?? res.data) as Record<string, unknown>
        const mapped = mapApiRequestToRequestOption(raw)
        if (mapped.requestDbId) setEditRequestDbId(mapped.requestDbId)
        if (mapped.serviceTypeId) applyServiceTypeSelection(mapped.serviceTypeId)
        if (mapped.description) setDescription(mapped.description)
        if (mapped.requestedQuantity) setRequestedQuantity(mapped.requestedQuantity)
        if (mapped.contactName) setContactName(mapped.contactName)
        if (mapped.contactNumber) setContactNumber(mapped.contactNumber)
        if (mapped.contactEmail) setContactEmail(mapped.contactEmail)
        if (mapped.preferredScheduleAt) setPreferredScheduleAt(toDatetimeLocalValue(mapped.preferredScheduleAt))
        if (mapped.preferredContactAt) setPreferredContactAt(toDatetimeLocalValue(mapped.preferredContactAt))
        if (mapped.requestSource) setRequestSource(mapped.requestSource)
        const payload = (raw.raw_payload ?? {}) as Record<string, unknown>
        const extracted = (raw.extracted_fields ?? {}) as Record<string, unknown>
        setTriage((prev) => ({
          ...prev,
          storeId: payload.store_id != null ? String(payload.store_id) : prev.storeId,
          storeCode: (payload.store_code as string) || prev.storeCode,
          siteAddress: mapped.siteAddress || (extracted.site_address as string) || prev.siteAddress,
          preferredScheduleUntil:
            toDatetimeLocalValue(mapped.preferredScheduleUntil) || prev.preferredScheduleUntil,
          contactAvailabilityText:
            mapped.contactAvailabilityText ||
            (extracted.contact_availability_text as string) ||
            prev.contactAvailabilityText,
          errorMessage: (extracted.error_message as string) || prev.errorMessage,
          workImpact: (extracted.work_impact as string) || prev.workImpact,
          othersAffected: (extracted.others_affected as string) || prev.othersAffected,
          userScope: (extracted.user_scope as string) || prev.userScope,
          appScope: (extracted.app_scope as string) || prev.appScope,
          accessType: (extracted.access_type as string) || prev.accessType,
          workPermitNeeded: (extracted.work_permit_needed as string) || prev.workPermitNeeded,
          permitAvailable: (extracted.permit_available as string) || prev.permitAvailable,
          permitDuration: (extracted.permit_duration as string) || prev.permitDuration,
          partsApproved: (extracted.parts_approved as string) || prev.partsApproved,
          closeOnFix: (extracted.close_on_fix as string) || prev.closeOnFix,
        }))
      } catch {
        // Prefill from selected request is enough for core fields
      }
    }

    void resolveAndEnrich()
    return () => {
      cancelled = true
    }
  }, [isOpen, isEditMode, initialRequest, clientCode, applyServiceTypeSelection])

  const runStoreSearch = useCallback(
    async (q: string) => {
      if (!clientCode || q.trim().length < 3) {
        setStoreResults([])
        return
      }
      setStoreLoading(true)
      try {
        const res = await TicketService.searchStoreOptions(q, clientCode)
        if (res.success && res.data) {
          setStoreResults(
            res.data.map((s) => ({
              storeId: s.storeId,
              storeCode: s.storeCode || '',
              storeName: s.storeName || '',
              branchAddress: s.branchAddress,
            })),
          )
        } else {
          setStoreResults([])
        }
      } catch {
        setStoreResults([])
      } finally {
        setStoreLoading(false)
      }
    },
    [clientCode],
  )

  useEffect(() => {
    if (storeSearchTimer.current) clearTimeout(storeSearchTimer.current)
    storeSearchTimer.current = setTimeout(() => {
      void runStoreSearch(storeQuery)
    }, 350)
    return () => {
      if (storeSearchTimer.current) clearTimeout(storeSearchTimer.current)
    }
  }, [storeQuery, runStoreSearch])

  const errors = useMemo(
    () =>
      computeFormErrors({
        clientId,
        serviceParent,
        hasChildTypes,
        serviceTypeId,
        description,
        contactName,
        contactNumber,
        contactEmail,
        requestedQuantity,
      }),
    [
      clientId,
      serviceParent,
      hasChildTypes,
      serviceTypeId,
      description,
      contactName,
      contactNumber,
      contactEmail,
      requestedQuantity,
    ],
  )

  const hasErrors = Object.keys(errors).length > 0

  const resetForm = () => {
    setClientId('')
    setServiceParent('')
    setServiceTypeId('')
    setDescription('')
    setRequestedQuantity(1)
    setContactName('')
    setContactNumber('')
    setContactEmail('')
    setPreferredScheduleAt('')
    setPreferredContactAt('')
    setRequestSource('email')
    setTriage(emptyTriage())
    setEditRequestDbId(null)
    setStoreQuery('')
    setStoreResults([])
    setTouched({})
    editPrefillKeyRef.current = null
    editEnrichKeyRef.current = null
  }

  const handleClose = () => {
    onClose()
    resetForm()
  }

  const handleSubmit = async () => {
    setTouched({
      client: true,
      serviceParent: true,
      serviceType: true,
      description: true,
      contactName: true,
      contactNumber: true,
      contactEmail: true,
      requestedQuantity: true,
    })
    const currentErrors = computeFormErrors({
      clientId,
      serviceParent,
      hasChildTypes,
      serviceTypeId,
      description,
      contactName,
      contactNumber,
      contactEmail,
      requestedQuantity,
    })
    if (Object.keys(currentErrors).length > 0 || !clientCode) {
      if (!clientCode && clientId) showError('Please select a client with a code.')
      focusFirstInvalidField(currentErrors, fieldRefMap)
      return
    }

    setIsSubmitting(true)
    try {
      if (isEditMode && !editRequestDbId) {
        showError('Unable to update request: missing request id. Re-select the request and try again.')
        return
      }

      const payload = {
        service_type_id: Number(effectiveServiceId),
        description: description.trim(),
        requested_quantity: requestedQuantity,
        preferred_schedule_at: toBackendDateTime(preferredScheduleAt),
        preferred_schedule_until: toBackendDateTime(triage.preferredScheduleUntil),
        contact_number: contactNumber.trim(),
        contact_name: contactName.trim(),
        contact_email: contactEmail.trim(),
        preferred_contact_at: toBackendDateTime(preferredContactAt),
        request_source: requestSource,
        store_id: triage.storeId ? Number(triage.storeId) : null,
        store_code: optionalString(triage.storeCode),
        site_address: optionalString(triage.siteAddress),
        error_message: optionalString(triage.errorMessage),
        work_impact: optionalString(triage.workImpact),
        others_affected: optionalString(triage.othersAffected),
        user_scope: optionalString(triage.userScope),
        app_scope: optionalString(triage.appScope),
        access_type: optionalString(triage.accessType),
        work_permit_needed: optionalString(triage.workPermitNeeded),
        permit_available: optionalString(triage.permitAvailable),
        permit_duration: optionalString(triage.permitDuration),
        parts_approved: optionalString(triage.partsApproved),
        close_on_fix: optionalString(triage.closeOnFix),
        contact_availability_text: optionalString(triage.contactAvailabilityText),
      }

      const headers = { 'X-Client-Code': clientCode }
      const res = isEditMode
        ? await apiClient.put(`/support/my-request/admin/${editRequestDbId}`, payload, { headers })
        : await apiClient.post('/support/my-request/admin', payload, { headers })

      const data = res.data?.data ?? res.data
      const request = toRequestOption(data as Record<string, unknown>)
      showSuccess(isEditMode ? 'Ticket request updated successfully.' : 'Ticket request created successfully.')
      handleClose()
      if (isEditMode) {
        onUpdated?.(request)
      } else {
        onCreated(request)
      }
    } catch (err: unknown) {
      const ax = err as {
        response?: { data?: { message?: string; errors?: Record<string, string[]> } }
        code?: string
        message?: string
      }
      if (!ax.response) {
        const apiUrl = import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000/api'
        const onLoopbackIp = typeof window !== 'undefined' && window.location.hostname === '127.0.0.1'
        showError(
          onLoopbackIp
            ? `Cannot reach the API at ${apiUrl}. Open the app at http://localhost:3001 (not 127.0.0.1) or add your origin to CORS_ALLOWED_ORIGINS in actionlabs-services/.env.`
            : `Cannot reach the API at ${apiUrl}. Ensure WAMP/Apache is running, or start the API with: php artisan serve --host=127.0.0.1 --port=8000`
        )
        return
      }
      const message =
        ax.response?.data?.message ??
        (isEditMode ? 'Failed to update ticket request' : 'Failed to create ticket request')
      const errs = ax.response?.data?.errors
      showError(errs ? Object.values(errs).flat().join(' ') : message)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} size="lg" placement="right">
      <DrawerOverlay />
      <DrawerContent maxW={{ base: '100%', md: '720px' }} display="flex" flexDirection="column">
        <DrawerCloseButton size="sm" top={2} right={2} />
        <DrawerHeader flexShrink={0} borderBottomWidth="1px" py={2.5} px={4} pr={10}>
          {isEditMode ? 'Edit Ticket Request' : 'Add Ticket Request'}
        </DrawerHeader>
        <DrawerBody flex="1" minH={0} overflowY="auto" px={4} py={3}>
          <VStack align="stretch" spacing={4}>
            <FormControl isInvalid={!!errors.client && touched.client} isRequired>
              <FormLabel>Client</FormLabel>
              {isLoadingClients ? (
                <HStack spacing={2} py={2}>
                  <Spinner size="sm" />
                  <Text fontSize="sm" color="gray.500">
                    Loading clients…
                  </Text>
                </HStack>
              ) : (
                <Select
                  ref={clientRef}
                  placeholder="Select client"
                  value={clientId}
                  isDisabled={isEditMode}
                  onChange={(e) => {
                    setClientId(e.target.value)
                    setServiceParent('')
                    setServiceTypeId('')
                    setTouched((p) => ({ ...p, client: true }))
                  }}
                >
                  {clients.map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {c.name || c.code || c.id}
                    </option>
                  ))}
                </Select>
              )}
              <FormErrorMessage>{errors.client}</FormErrorMessage>
            </FormControl>

            {clientCode && (
              <>
                <FormControl
                  isInvalid={!!errors.serviceParent && touched.serviceParent}
                  isRequired
                  isDisabled={isLoadingOptions}
                >
                  <FormLabel>Service Type</FormLabel>
                  <Select
                    ref={serviceParentRef}
                    placeholder="Select service type"
                    value={serviceParent}
                    onChange={(e) => {
                      setServiceParent(e.target.value)
                      setServiceTypeId('')
                      setTouched((p) => ({ ...p, serviceParent: true }))
                    }}
                  >
                    {parentOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>{errors.serviceParent}</FormErrorMessage>
                </FormControl>

                {hasChildTypes && (
                  <FormControl isInvalid={!!errors.serviceType && touched.serviceType} isRequired>
                    <FormLabel>Subcategory</FormLabel>
                    <Select
                      ref={serviceTypeRef}
                      placeholder="Select subcategory"
                      value={serviceTypeId}
                      onChange={(e) => {
                        setServiceTypeId(e.target.value)
                        setTouched((p) => ({ ...p, serviceType: true }))
                      }}
                    >
                      {childOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage>{errors.serviceType}</FormErrorMessage>
                  </FormControl>
                )}

                <FormControl isInvalid={!!errors.description && touched.description} isRequired>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    ref={descriptionRef}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    onBlur={() => setTouched((p) => ({ ...p, description: true }))}
                    placeholder="Issue description"
                    minH="80px"
                  />
                  <FormErrorMessage>{errors.description}</FormErrorMessage>
                </FormControl>

                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <FormControl isInvalid={!!errors.requestedQuantity && touched.requestedQuantity} isRequired>
                    <FormLabel>Devices Affected</FormLabel>
                    <NumberInput
                      value={requestedQuantity}
                      min={1}
                      onChange={(_, n) => setRequestedQuantity(Number.isNaN(n) ? 1 : n)}
                      onBlur={() => setTouched((p) => ({ ...p, requestedQuantity: true }))}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                    <FormErrorMessage>{errors.requestedQuantity}</FormErrorMessage>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Request Source</FormLabel>
                    <Select value={requestSource} onChange={(e) => setRequestSource(e.target.value)}>
                      {requestSourceOptions.map((o) => (
                        <option key={o.code} value={o.code}>
                          {o.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <FormControl>
                    <FormLabel>Preferred Schedule</FormLabel>
                    <Input
                      type="datetime-local"
                      value={preferredScheduleAt}
                      onChange={(e) => setPreferredScheduleAt(e.target.value)}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Preferred Contact Date & Time</FormLabel>
                    <Input
                      type="datetime-local"
                      value={preferredContactAt}
                      onChange={(e) => setPreferredContactAt(e.target.value)}
                    />
                  </FormControl>
                </Grid>

                <Box borderWidth="1px" borderRadius="md" p={3} bg="gray.50">
                  <Button
                    variant="ghost"
                    size="sm"
                    width="100%"
                    justifyContent="space-between"
                    onClick={() => patchTriage({ triageExpanded: !triage.triageExpanded })}
                    fontWeight="600"
                    color="blue.900"
                  >
                    Triage details
                    <Text as="span" fontSize="xs" color="gray.500">
                      {triage.triageExpanded ? 'Hide' : 'Show'} optional fields
                    </Text>
                  </Button>

                  <Collapse in={triage.triageExpanded} animateOpacity>
                    <Grid templateColumns="1fr" gap={3} mt={3}>
                      <GridItem>
                        <FormControl>
                          <FormLabel fontSize="sm">Exact error message (optional)</FormLabel>
                          <Input
                            value={triage.errorMessage}
                            onChange={(e) => patchTriage({ errorMessage: e.target.value })}
                            placeholder="e.g. Paper jam error E3"
                            maxLength={200}
                          />
                        </FormControl>
                      </GridItem>

                      <GridItem>
                        <FormControl>
                          <FormLabel fontSize="sm">Dispatch until</FormLabel>
                          <Input
                            type="datetime-local"
                            value={triage.preferredScheduleUntil}
                            onChange={(e) => patchTriage({ preferredScheduleUntil: e.target.value })}
                          />
                        </FormControl>
                      </GridItem>

                      <GridItem>
                        <FormControl>
                          <FormLabel fontSize="sm">Preferred contact window (optional)</FormLabel>
                          <Input
                            value={triage.contactAvailabilityText}
                            onChange={(e) => patchTriage({ contactAvailabilityText: e.target.value })}
                            placeholder="e.g. Weekdays 9 AM – 5 PM"
                          />
                        </FormControl>
                      </GridItem>

                      {(showOnsite || triage.storeCode || triage.siteAddress) && (
                        <>
                          <GridItem>
                            <FormControl>
                              <FormLabel fontSize="sm">Store / site</FormLabel>
                              <Input
                                value={storeQuery}
                                onChange={(e) => {
                                  setStoreQuery(e.target.value)
                                  patchTriage({ storeLabel: e.target.value, storeCode: '', storeId: '' })
                                }}
                                placeholder="Search by store name or code (min 3 characters)"
                              />
                              {storeLoading && <FormHelperText>Searching stores...</FormHelperText>}
                              {storeResults.length > 0 && (
                                <List mt={1} borderWidth="1px" borderRadius="md" bg="white" maxH="160px" overflowY="auto">
                                  {storeResults.map((s) => (
                                    <ListItem
                                      key={`${s.storeCode}-${s.storeId}`}
                                      px={3}
                                      py={2}
                                      cursor="pointer"
                                      _hover={{ bg: 'blue.50' }}
                                      onClick={() => {
                                        const label = `${s.storeCode} — ${s.storeName}`
                                        setStoreQuery(label)
                                        setStoreResults([])
                                        patchTriage({
                                          storeId: s.storeId != null ? String(s.storeId) : '',
                                          storeCode: s.storeCode,
                                          storeLabel: label,
                                          siteAddress: s.branchAddress || triage.siteAddress || '',
                                        })
                                      }}
                                    >
                                      <Text fontSize="sm" fontWeight="600">
                                        {s.storeCode} — {s.storeName}
                                      </Text>
                                      {s.branchAddress && (
                                        <Text fontSize="xs" color="gray.600">
                                          {s.branchAddress}
                                        </Text>
                                      )}
                                    </ListItem>
                                  ))}
                                </List>
                              )}
                            </FormControl>
                          </GridItem>

                          <GridItem>
                            <FormControl>
                              <FormLabel fontSize="sm">Site address (if not listed above)</FormLabel>
                              <Input
                                value={triage.siteAddress}
                                onChange={(e) => patchTriage({ siteAddress: e.target.value })}
                                placeholder="Building, floor, or full address"
                              />
                            </FormControl>
                          </GridItem>
                        </>
                      )}

                      <GridItem>
                        <FormControl>
                          <FormLabel fontSize="sm">How is this affecting work?</FormLabel>
                          <Textarea
                            value={triage.workImpact}
                            onChange={(e) => patchTriage({ workImpact: e.target.value })}
                            rows={2}
                          />
                        </FormControl>
                      </GridItem>

                      <GridItem>
                        <RadioRow
                          label="Others experiencing the same issue?"
                          value={triage.othersAffected}
                          options={othersAffectedOptions}
                          onChange={(v) => patchTriage({ othersAffected: v })}
                        />
                      </GridItem>

                      <GridItem>
                        <RadioRow
                          label="Users affected"
                          value={triage.userScope}
                          options={userScopeOptions}
                          onChange={(v) => patchTriage({ userScope: v })}
                        />
                      </GridItem>

                      <GridItem>
                        <RadioRow
                          label="Applications affected"
                          value={triage.appScope}
                          options={appScopeOptions}
                          onChange={(v) => patchTriage({ appScope: v })}
                        />
                      </GridItem>

                      <GridItem>
                        <RadioRow
                          label="Access type"
                          value={triage.accessType}
                          options={accessTypeOptions}
                          onChange={(v) => patchTriage({ accessType: v })}
                        />
                      </GridItem>

                      {showOnsite && (
                        <>
                          <GridItem>
                            <RadioRow
                              label="Work permit needed?"
                              value={triage.workPermitNeeded}
                              options={workPermitNeededOptions}
                              onChange={(v) => patchTriage({ workPermitNeeded: v })}
                            />
                          </GridItem>

                          {triage.workPermitNeeded === 'Yes' && (
                            <>
                              <GridItem>
                                <RadioRow
                                  label="Permit readily available?"
                                  value={triage.permitAvailable}
                                  options={permitAvailableOptions}
                                  onChange={(v) => patchTriage({ permitAvailable: v })}
                                />
                              </GridItem>
                              <GridItem>
                                <FormControl>
                                  <FormLabel fontSize="sm">Permit processing time</FormLabel>
                                  <Input
                                    value={triage.permitDuration}
                                    onChange={(e) => patchTriage({ permitDuration: e.target.value })}
                                    placeholder="e.g. 2 business days"
                                  />
                                </FormControl>
                              </GridItem>
                            </>
                          )}

                          <GridItem>
                            <RadioRow
                              label="Parts approved?"
                              value={triage.partsApproved}
                              options={partsApprovedOptions}
                              onChange={(v) => patchTriage({ partsApproved: v })}
                            />
                          </GridItem>
                        </>
                      )}

                      <GridItem>
                        <RadioRow
                          label="Close ticket when fixed?"
                          value={triage.closeOnFix}
                          options={closeOnFixOptions}
                          onChange={(v) => patchTriage({ closeOnFix: v })}
                        />
                      </GridItem>
                    </Grid>
                  </Collapse>
                </Box>

                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <FormControl isInvalid={!!errors.contactName && touched.contactName} isRequired>
                    <FormLabel>Contact Name</FormLabel>
                    <Input
                      ref={contactNameRef}
                      value={contactName}
                      onChange={(e) => {
                        const filtered = e.target.value.replace(/[0-9]/g, '')
                        setContactName(filtered)
                        setTouched((p) => ({ ...p, contactName: true }))
                      }}
                      onBlur={() => setTouched((p) => ({ ...p, contactName: true }))}
                      placeholder="Contact person"
                    />
                    <FormErrorMessage>{errors.contactName}</FormErrorMessage>
                  </FormControl>
                  <FormControl isInvalid={!!errors.contactNumber && touched.contactNumber} isRequired>
                    <FormLabel>Contact Number</FormLabel>
                    <Input
                      ref={contactNumberRef}
                      value={contactNumber}
                      onChange={(e) => {
                        const filtered = e.target.value.replace(/[^0-9+\-() ]/g, '')
                        setContactNumber(filtered)
                        setTouched((p) => ({ ...p, contactNumber: true }))
                      }}
                      onBlur={() => setTouched((p) => ({ ...p, contactNumber: true }))}
                      placeholder="Phone number"
                      inputMode="tel"
                    />
                    <FormErrorMessage>{errors.contactNumber}</FormErrorMessage>
                  </FormControl>
                </Grid>

                <FormControl isInvalid={!!errors.contactEmail && touched.contactEmail} isRequired>
                  <FormLabel>Contact Email</FormLabel>
                  <Input
                    ref={contactEmailRef}
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    onBlur={() => setTouched((p) => ({ ...p, contactEmail: true }))}
                    placeholder="email@example.com"
                  />
                  <FormErrorMessage>{errors.contactEmail}</FormErrorMessage>
                </FormControl>
              </>
            )}

            {!clientId && (
              <Text fontSize="sm" color="gray.500">
                Select a client to load service types and subcategories.
              </Text>
            )}
          </VStack>
        </DrawerBody>
        <DrawerFooter borderTopWidth="1px" gap={2} flexShrink={0} px={4} py={2.5}>
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={() => void handleSubmit()}
            isLoading={isSubmitting}
            isDisabled={hasErrors || !clientCode || (isEditMode && !editRequestDbId)}
          >
            {isEditMode ? 'Save Changes' : 'Create Request'}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}

export default CreateTicketRequestModal
